import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
  Checkbox,
  Tooltip,
  Divider,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  LinearProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  People as PeopleIcon,
  PersonOff as PersonOffIcon,
  AssignmentInd as AssignmentIndIcon,
  FilterAlt as FilterAltIcon,
  CheckBox as CheckBoxIcon,
  IndeterminateCheckBox as IndeterminateCheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
} from '@mui/icons-material';
import { getAssignmentSummary, getAssignmentList, assignProject, bulkAssignProjects, getUsers } from '../services/api';
import PageSearchBar from '../components/PageSearchBar';

// ─── Constants ────────────────────────────────────────────────────────────────

const PHASE_LABELS = {
  1: 'Proposal',
  2: 'Engineering',
  3: 'Rough-in',
  4: 'Pre-Final',
  5: 'Final',
  6: 'Final Billing',
  7: 'AfterCare',
};

const PHASE_COLORS = {
  1: '#e3f2fd',
  2: '#e8f5e9',
  3: '#fff3e0',
  4: '#fce4ec',
  5: '#f3e5f5',
  6: '#e0f2f1',
};

// ─── Summary cards ─────────────────────────────────────────────────────────────

const SummaryCard = ({ icon, label, value, color, onClick, active }) => (
  <Card
    elevation={0}
    onClick={onClick}
    sx={{
      border: active ? `2px solid ${color}` : '1px solid #e0e0e0',
      borderRadius: 2,
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 0.15s',
      '&:hover': onClick ? { boxShadow: 2, borderColor: color } : {},
      bgcolor: active ? `${color}18` : 'white',
    }}
  >
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ color, fontSize: 36, lineHeight: 1 }}>{icon}</Box>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ color, lineHeight: 1 }}>
            {value ?? <CircularProgress size={24} />}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// ─── PM Distribution table ─────────────────────────────────────────────────────

const PMDistributionTable = ({ byPm, onPmFilter, activePmId }) => (
  <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, maxHeight: 320, overflow: 'auto' }}>
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow sx={{ '& th': { bgcolor: '#f5f5f5', fontWeight: 700 } }}>
          <TableCell>PM Name</TableCell>
          <TableCell align="right">Projects</TableCell>
          <TableCell align="right">Filter</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {byPm.map((pm) => (
          <TableRow
            key={pm.id}
            hover
            sx={{
              bgcolor: activePmId === String(pm.id) ? '#e3f2fd' : 'inherit',
              cursor: 'pointer',
            }}
            onClick={() => onPmFilter(activePmId === String(pm.id) ? null : String(pm.id))}
          >
            <TableCell>
              <Typography variant="body2" fontWeight={pm.project_count > 0 ? 600 : 400}>
                {pm.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">{pm.role}</Typography>
            </TableCell>
            <TableCell align="right">
              <Chip
                label={pm.project_count}
                size="small"
                color={pm.project_count > 100 ? 'error' : pm.project_count > 50 ? 'warning' : 'default'}
                sx={{ fontWeight: 700, minWidth: 40 }}
              />
            </TableCell>
            <TableCell align="right">
              {activePmId === String(pm.id) ? (
                <Chip label="Active" size="small" color="primary" />
              ) : (
                <Chip label="Filter" size="small" variant="outlined" />
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

// ─── Inline PM/Sales select cell ──────────────────────────────────────────────

const AssignSelect = ({ value, options, onChange, label, loading }) => (
  <FormControl size="small" sx={{ minWidth: 160 }} disabled={loading}>
    <Select
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
      displayEmpty
      renderValue={(v) => {
        if (!v) return <Typography variant="caption" color="text.secondary">— {label} —</Typography>;
        const found = options.find((o) => String(o.id) === String(v));
        return <Typography variant="caption">{found?.name || v}</Typography>;
      }}
      sx={{ fontSize: '0.8rem' }}
    >
      <MenuItem value="">
        <Typography variant="caption" color="text.secondary">— Unassigned —</Typography>
      </MenuItem>
      {options.map((o) => (
        <MenuItem key={o.id} value={String(o.id)}>
          <Typography variant="body2">{o.name}</Typography>
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

// ─── Bulk assign dialog ────────────────────────────────────────────────────────

const BulkAssignDialog = ({ open, onClose, onConfirm, count, pmOptions, salesOptions }) => {
  const [pmId, setPmId] = useState('');
  const [salesId, setSalesId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setPmId(''); setSalesId(''); }
  }, [open]);

  const handleConfirm = async () => {
    const payload = {};
    if (pmId) payload.pm_id = parseInt(pmId, 10);
    if (salesId) payload.sales_id = parseInt(salesId, 10);
    if (!pmId && !salesId) return;
    setSaving(true);
    try {
      await onConfirm(payload);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Bulk Assign — {count} projects</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Leave a field blank to leave it unchanged. Only the selected fields will be updated.
        </Typography>

        <FormControl size="small" fullWidth>
          <InputLabel>Assign PM</InputLabel>
          <Select value={pmId} onChange={(e) => setPmId(e.target.value)} label="Assign PM">
            <MenuItem value=""><em>Leave unchanged</em></MenuItem>
            {pmOptions.map((o) => (
              <MenuItem key={o.id} value={String(o.id)}>{o.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" fullWidth>
          <InputLabel>Assign Sales Rep</InputLabel>
          <Select value={salesId} onChange={(e) => setSalesId(e.target.value)} label="Assign Sales Rep">
            <MenuItem value=""><em>Leave unchanged</em></MenuItem>
            {salesOptions.map((o) => (
              <MenuItem key={o.id} value={String(o.id)}>{o.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={saving || (!pmId && !salesId)}
        >
          {saving ? <CircularProgress size={18} /> : `Apply to ${count} projects`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main Assignments component ────────────────────────────────────────────────

const Assignments = ({ embedded = false }) => {
  // Summary
  const [summary, setSummary] = useState(null);

  // Users
  const [pmOptions, setPmOptions] = useState([]);
  const [salesOptions, setSalesOptions] = useState([]);

  // Table data
  const [projects, setProjects] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [tableLoading, setTableLoading] = useState(true);

  // Filters
  const [filterPmId, setFilterPmId] = useState(null);     // string | null
  const [filterSalesId, setFilterSalesId] = useState(null);
  const [filterNoPm, setFilterNoPm] = useState(false);
  const [filterNoSales, setFilterNoSales] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Selection
  const [selected, setSelected] = useState(new Set());

  // In-progress saves per row (project id)
  const [savingRow, setSavingRow] = useState(new Set());

  // Bulk assign dialog
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);

  // Snackbar
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const showSnack = (message, severity = 'success') =>
    setSnack({ open: true, message, severity });

  // ── Load users once ────────────────────────────────────────────────────────

  useEffect(() => {
    getUsers()
      .then((res) => {
        const users = res.data || [];
        // PM dropdown: PMs + anyone already assigned as PM
        const pmRoles = ['PM'];
        const pmIds = new Set(users.filter(u => pmRoles.includes(u.role)).map(u => u.id));
        setPmOptions(users.filter((u) => pmRoles.includes(u.role) || pmIds.has(u.id)).sort((a, b) => a.name.localeCompare(b.name)));
        // Sales dropdown: Sales team + leadership who sell (Tom, Robert, Jonathan, Bill, Christine, Vin, Engjell)
        const salesEligibleIds = new Set([1, 2, 3, 5, 8, 43, 52]);
        setSalesOptions(users.filter((u) => u.role === 'Sales' || salesEligibleIds.has(u.id)).sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(() => {});
  }, []);

  // ── Load summary ───────────────────────────────────────────────────────────

  const loadSummary = useCallback(() => {
    getAssignmentSummary()
      .then((res) => setSummary(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  // ── Debounce search ────────────────────────────────────────────────────────

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchText), 350);
    return () => clearTimeout(t);
  }, [searchText]);

  // Reset page on filter change
  useEffect(() => { setPage(0); setSelected(new Set()); }, [filterPmId, filterSalesId, filterNoPm, filterNoSales, debouncedSearch]);

  // ── Load project list ──────────────────────────────────────────────────────

  const loadProjects = useCallback(() => {
    setTableLoading(true);
    const params = {
      page: page + 1,
      limit: rowsPerPage,
    };
    if (filterPmId) params.pm_id = filterPmId;
    if (filterSalesId) params.sales_id = filterSalesId;
    if (filterNoPm) params.no_pm = 'true';
    if (filterNoSales) params.no_sales = 'true';
    if (debouncedSearch) params.search = debouncedSearch;

    getAssignmentList(params)
      .then((res) => {
        setProjects(res.data.projects || []);
        setTotalCount(res.data.total || 0);
      })
      .catch(() => showSnack('Failed to load projects', 'error'))
      .finally(() => setTableLoading(false));
  }, [page, rowsPerPage, filterPmId, filterSalesId, filterNoPm, filterNoSales, debouncedSearch]);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  // ── Inline assign ──────────────────────────────────────────────────────────

  const handleInlineAssign = async (projectId, field, value) => {
    setSavingRow((prev) => new Set(prev).add(projectId));
    try {
      const payload = { [field]: value ? parseInt(value, 10) : null };
      const res = await assignProject(projectId, payload);
      setProjects((prev) =>
        prev.map((p) =>
          p.id === projectId
            ? { ...p, pm_id: res.data.pm_id, pm_name: res.data.pm_name, sales_id: res.data.sales_id, sales_name: res.data.sales_name }
            : p
        )
      );
      loadSummary();
    } catch {
      showSnack('Failed to save assignment', 'error');
    } finally {
      setSavingRow((prev) => { const s = new Set(prev); s.delete(projectId); return s; });
    }
  };

  // ── Bulk assign ────────────────────────────────────────────────────────────

  const handleBulkAssign = async (payload) => {
    const ids = Array.from(selected);
    try {
      await bulkAssignProjects({ project_ids: ids, ...payload });
      showSnack(`Updated ${ids.length} projects successfully`);
      setSelected(new Set());
      loadProjects();
      loadSummary();
    } catch {
      showSnack('Bulk assignment failed', 'error');
    }
  };

  // ── Selection helpers ──────────────────────────────────────────────────────

  const allSelected = projects.length > 0 && projects.every((p) => selected.has(p.id));
  const someSelected = projects.some((p) => selected.has(p.id)) && !allSelected;

  const toggleAll = () => {
    if (allSelected) {
      const next = new Set(selected);
      projects.forEach((p) => next.delete(p.id));
      setSelected(next);
    } else {
      const next = new Set(selected);
      projects.forEach((p) => next.add(p.id));
      setSelected(next);
    }
  };

  const toggleRow = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  // ── Filter helpers ─────────────────────────────────────────────────────────

  const clearFilters = () => {
    setFilterPmId(null);
    setFilterSalesId(null);
    setFilterNoPm(false);
    setFilterNoSales(false);
    setSearchText('');
  };

  const activeFilterCount = [filterPmId, filterSalesId, filterNoPm, filterNoSales, debouncedSearch]
    .filter(Boolean).length;

  // PM name lookup for the "filter by PM" display
  const activePmName = useMemo(() => {
    if (!filterPmId) return null;
    return summary?.by_pm?.find((p) => String(p.id) === filterPmId)?.name || filterPmId;
  }, [filterPmId, summary]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* ── Page header ── */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 300, color: '#0D2C54', letterSpacing: '-0.5px' }}>
          PM &amp; Sales Assignments
        </Typography>
        <Typography variant="body2" sx={{ color: '#8898A4', mt: 0.5 }}>
          Manage project manager and sales rep assignments across all active projects
        </Typography>
      </Box>

      {/* ── Summary cards row ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            icon={<PeopleIcon fontSize="inherit" />}
            label="Total Active Projects"
            value={summary?.totals?.total_active}
            color="#1565c0"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            icon={<PersonOffIcon fontSize="inherit" />}
            label="No PM Assigned"
            value={summary?.totals?.no_pm}
            color="#e65100"
            onClick={() => { setFilterNoPm(!filterNoPm); setFilterNoSales(false); setFilterPmId(null); }}
            active={filterNoPm}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            icon={<PersonOffIcon fontSize="inherit" />}
            label="No Sales Rep Assigned"
            value={summary?.totals?.no_sales}
            color="#c62828"
            onClick={() => { setFilterNoSales(!filterNoSales); setFilterNoPm(false); setFilterPmId(null); }}
            active={filterNoSales}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            icon={<AssignmentIndIcon fontSize="inherit" />}
            label="PMs in Roster"
            value={summary?.by_pm?.length}
            color="#2e7d32"
          />
        </Grid>
      </Grid>

      {/* ── PM Distribution panel ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={5}>
          <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
              Projects per PM
              {filterPmId && (
                <Chip
                  label={`Filtered: ${activePmName}`}
                  size="small"
                  color="primary"
                  onDelete={() => setFilterPmId(null)}
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>
            {summary ? (
              <PMDistributionTable
                byPm={summary.by_pm}
                onPmFilter={(id) => { setFilterPmId(id); setFilterNoPm(false); setFilterNoSales(false); }}
                activePmId={filterPmId}
              />
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
              Projects per Sales Rep
            </Typography>
            {summary ? (
              <TableContainer sx={{ maxHeight: 320, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: '#f5f5f5', fontWeight: 700 } }}>
                      <TableCell>Sales Rep</TableCell>
                      <TableCell align="right">Projects</TableCell>
                      <TableCell align="right">Filter</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {summary.by_sales.map((s) => (
                      <TableRow
                        key={s.id}
                        hover
                        sx={{
                          bgcolor: filterSalesId === String(s.id) ? '#e8f5e9' : 'inherit',
                          cursor: 'pointer',
                        }}
                        onClick={() => {
                          setFilterSalesId(filterSalesId === String(s.id) ? null : String(s.id));
                          setFilterNoPm(false);
                          setFilterNoSales(false);
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={s.project_count > 0 ? 600 : 400}>{s.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{s.role}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip label={s.project_count} size="small" sx={{ fontWeight: 700, minWidth: 40 }} />
                        </TableCell>
                        <TableCell align="right">
                          {filterSalesId === String(s.id) ? (
                            <Chip label="Active" size="small" color="success" />
                          ) : (
                            <Chip label="Filter" size="small" variant="outlined" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* ── Bulk assignment table ── */}
      <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
        {/* Table toolbar */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mr: 1 }}>
            Project List
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              ({totalCount} matching)
            </Typography>
          </Typography>

          {/* Search */}
          <PageSearchBar
            placeholder="Search name, client, job #..."
            value={searchText}
            onChange={(v) => setSearchText(v)}
            onClear={() => setSearchText('')}
          />

          {/* PM Filter */}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Filter by PM</InputLabel>
            <Select
              value={filterPmId || ''}
              onChange={(e) => { setFilterPmId(e.target.value || null); setFilterNoPm(false); }}
              label="Filter by PM"
            >
              <MenuItem value=""><em>All PMs</em></MenuItem>
              {summary?.by_pm?.map((pm) => (
                <MenuItem key={pm.id} value={String(pm.id)}>
                  {pm.name} ({pm.project_count})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Sales Filter */}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Filter by Sales</InputLabel>
            <Select
              value={filterSalesId || ''}
              onChange={(e) => { setFilterSalesId(e.target.value || null); setFilterNoSales(false); }}
              label="Filter by Sales"
            >
              <MenuItem value=""><em>All Sales Reps</em></MenuItem>
              {summary?.by_sales?.map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>
                  {s.name} ({s.project_count})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Quick filters */}
          <Chip
            label="No PM"
            icon={<FilterAltIcon fontSize="small" />}
            onClick={() => { setFilterNoPm(!filterNoPm); setFilterNoSales(false); setFilterPmId(null); }}
            color={filterNoPm ? 'warning' : 'default'}
            variant={filterNoPm ? 'filled' : 'outlined'}
            size="small"
          />
          <Chip
            label="No Sales"
            icon={<FilterAltIcon fontSize="small" />}
            onClick={() => { setFilterNoSales(!filterNoSales); setFilterNoPm(false); setFilterSalesId(null); }}
            color={filterNoSales ? 'error' : 'default'}
            variant={filterNoSales ? 'filled' : 'outlined'}
            size="small"
          />

          {activeFilterCount > 0 && (
            <Button size="small" startIcon={<ClearIcon />} onClick={clearFilters} sx={{ ml: 'auto' }}>
              Clear filters ({activeFilterCount})
            </Button>
          )}

          {/* Bulk assign button */}
          {selected.size > 0 && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AssignmentIndIcon />}
              onClick={() => setBulkDialogOpen(true)}
              sx={{ ml: activeFilterCount > 0 ? 0 : 'auto' }}
            >
              Bulk Assign ({selected.size})
            </Button>
          )}
        </Box>

        {tableLoading && <LinearProgress />}

        {/* Table */}
        <TableContainer sx={{ maxHeight: 600, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: '#f5f5f5', fontWeight: 700 } }}>
                <TableCell padding="checkbox" sx={{ bgcolor: '#f5f5f5' }}>
                  <Tooltip title={allSelected ? 'Deselect all on page' : 'Select all on page'}>
                    <Checkbox
                      indeterminate={someSelected}
                      checked={allSelected}
                      onChange={toggleAll}
                      icon={<CheckBoxOutlineBlankIcon />}
                      checkedIcon={<CheckBoxIcon />}
                      indeterminateIcon={<IndeterminateCheckBoxIcon />}
                      size="small"
                    />
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ minWidth: 200 }}>Project</TableCell>
                <TableCell sx={{ minWidth: 110 }}>Job #</TableCell>
                <TableCell sx={{ minWidth: 160 }}>Client</TableCell>
                <TableCell sx={{ minWidth: 80 }}>Phase</TableCell>
                <TableCell sx={{ minWidth: 90 }}>Started</TableCell>
                <TableCell sx={{ minWidth: 180 }}>Project Manager</TableCell>
                <TableCell sx={{ minWidth: 180 }}>Sales Rep</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projects.length === 0 && !tableLoading && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No projects match the current filters.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {projects.map((p) => {
                const isSelected = selected.has(p.id);
                const isSaving = savingRow.has(p.id);
                return (
                  <TableRow
                    key={p.id}
                    hover
                    selected={isSelected}
                    sx={{
                      bgcolor: isSelected ? '#e3f2fd' : 'inherit',
                      opacity: isSaving ? 0.6 : 1,
                      transition: 'opacity 0.2s',
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => toggleRow(p.id)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 200 }}>
                        {p.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {p.primary_job_number || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
                        {p.client_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={PHASE_LABELS[p.current_phase] || `Phase ${p.current_phase}`}
                        size="small"
                        sx={{
                          bgcolor: PHASE_COLORS[p.current_phase] || '#f5f5f5',
                          fontSize: '0.7rem',
                          height: 20,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {p.started_at ? new Date(p.started_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <AssignSelect
                        value={p.pm_id}
                        options={pmOptions}
                        onChange={(val) => handleInlineAssign(p.id, 'pm_id', val)}
                        label="PM"
                        loading={isSaving}
                      />
                    </TableCell>
                    <TableCell>
                      <AssignSelect
                        value={p.sales_id}
                        options={salesOptions}
                        onChange={(val) => handleInlineAssign(p.id, 'sales_id', val)}
                        label="Sales"
                        loading={isSaving}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider />
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />
      </Paper>

      {/* ── Bulk assign dialog ── */}
      <BulkAssignDialog
        open={bulkDialogOpen}
        onClose={() => setBulkDialogOpen(false)}
        onConfirm={handleBulkAssign}
        count={selected.size}
        pmOptions={pmOptions}
        salesOptions={salesOptions}
      />

      {/* ── Snackbar ── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Assignments;
